import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSelector } from 'react-redux';
import { useOutsideClick } from '../../hooks/useOutsideClick';

const CustomSelect = ({
    value,
    onChange,
    options = [],
    isError,
    label,
    disabled,
    rightLabel,
    rightOnCLick,
    placeholder,
    defaultLabel,
    required,
    className,
}) => {
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleSelect = option => {
        onChange(option);
        setOpen(false);
    };

    useOutsideClick(wrapperRef, () => {
        if (open) setOpen(false);
    });

    const clientData = useSelector(state => state.juno.clientData);
    const { readOnly } = clientData || {};

    return (
        <div className={`relative w-full ${className || ''}`} ref={wrapperRef}>
            <label className="block text-sm text-balanceCard-manage mb-2 uppercase tracking-wide w-full text-start">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
                {rightLabel ? (
                    <span onClick={rightOnCLick} className="float-right cursor-pointer text-widget">
                        {rightLabel}
                    </span>
                ) : null}
            </label>
            <div
            data-e2e={value?.currencyShortName}
                key={value?.currencyShortName + value?.blockchain}
                className={`${
                    options?.length > 1 && !disabled && !readOnly
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed'
                } relative bg-card-bg !text-text rounded-md py-4 pl-14 pr-20 text-sm font-medium border-2 
                ${isError ? 'border-red-400' : 'border-transparent'}`}
                onClick={disabled || readOnly || options?.length < 2 ? null : handleToggle}>
                <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {value?.currencyIcon && (
                        <img
                            src={`${imageBaseUrl}${value?.currencyIcon}`}
                            alt=""
                            className="w-5 h-5 rounded-full"
                        />
                    )}
                    <span className="text-text text-sm  uppercase" data-e2e={value?.currencyShortName || value || placeholder}>
                        {value?.currencyShortName || value || placeholder}
                    </span>
                    {value?.currencyFullName || value?.blockchain ? (
                        <span>({value?.currencyFullName || value?.blockchain})</span>
                    ) : defaultLabel ? (
                        <div className="!text-text">{defaultLabel}</div>
                    ) : null}
                </div>

                {options?.length > 1 ? (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-white">
                        {open ? (
                            <ExpandLessIcon fontSize="small" className="invert dark:invert-0" />
                        ) : (
                            <ExpandMoreIcon fontSize="small" className="invert dark:invert-0" />
                        )}
                    </div>
                ) : null}
            </div>

            {open && !disabled ? (
                <ul className="absolute w-full max-h-[200px] z-10 mt-1 bg-white dark:!bg-[#26272B] rounded-md shadow-lg overflow-auto !text-text text-sm hide-scrollbar">
                    {options.map(opt => (
                        <li
                            key={opt.currencyShortName || opt.blockchain || opt}
                            onClick={() => handleSelect(opt)}
                            className="px-4 py-2 cursor-pointer hover:bg-card-bg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {opt.currencyIcon && (
                                    <img
                                        src={`${imageBaseUrl}${opt.currencyIcon}`}
                                        alt=""
                                        className="w-5 h-5 rounded-full"
                                    />
                                )}
                                <span data-e2e={opt.currencyShortName || opt}>{opt.currencyShortName || opt}</span>
                                {opt.currencyFullName || opt.blockchain ? (
                                    <span>({opt.currencyFullName || opt.blockchain})</span>
                                ) : null}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

CustomSelect.propTypes = {
    value: PropTypes.shape({
        currencyIcon: PropTypes.string,
        currencyShortName: PropTypes.string,
        currencySymbol: PropTypes.string,
        balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            currencyIcon: PropTypes.string,
            currencyShortName: PropTypes.string.isRequired,
            currencyFullName: PropTypes.string.isRequired,
            currencySymbol: PropTypes.string.isRequired,
            balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    isError: PropTypes.bool,
};

export default CustomSelect;
